import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const useAirKey = () => {
  const { isLoading, error, sendRequest } = useHttp();
  const authCtx = useContext(AuthContext);

  const sync = useCallback(async () => {
    const response = await sendRequest({
      url: '/api/v1/airkey/sync',
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
    });
    return response;
  }, [sendRequest, authCtx]);

  const autoMatch = useCallback(async () => {
    const response = await sendRequest({
      url: '/api/v1/airkey/auto-match',
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
    });
    return response;
  }, [sendRequest, authCtx]);

  const manualMatch = useCallback(
    async (airKeyPersonId, userId) => {
      const response = await sendRequest({
        url: `/api/v1/airkey/match/${airKeyPersonId}`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { userId },
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const unmatch = useCallback(
    async airKeyPersonId => {
      const response = await sendRequest({
        url: `/api/v1/airkey/unmatch/${airKeyPersonId}`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const fetchPersons = useCallback(
    async ({ page = 1, limit = 50, search = '', sortField = 'lastName', sortDir = 'asc', view = 'unmatched' } = {}) => {
      const response = await sendRequest({
        url: '/api/v1/airkey/unmatched',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params: { page, limit, search, sortField, sortDir, view },
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const fetchPersonProtocol = useCallback(
    async (airKeyPersonId, { from, to } = {}) => {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const response = await sendRequest({
        url: `/api/v1/airkey/protocol/${airKeyPersonId}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const fetchByUserId = useCallback(
    async userId => {
      const response = await sendRequest({
        url: `/api/v1/airkey/by-user/${userId}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const fetchAll = useCallback(async () => {
    const response = await sendRequest({
      url: '/api/v1/airkey/persons',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });
    return response;
  }, [sendRequest, authCtx]);

  const fetchAreas = useCallback(async () => {
    const response = await sendRequest({
      url: '/api/v1/airkey/areas',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });
    return response;
  }, [sendRequest, authCtx]);

  const fetchBranchMappings = useCallback(async () => {
    const response = await sendRequest({
      url: '/api/v1/airkey/branch-mappings',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });
    return response;
  }, [sendRequest, authCtx]);

  const upsertBranchMapping = useCallback(
    async mappingData => {
      const response = await sendRequest({
        url: '/api/v1/airkey/branch-mappings',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: mappingData,
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const deleteBranchMapping = useCallback(
    async mappingId => {
      const response = await sendRequest({
        url: `/api/v1/airkey/branch-mappings/${mappingId}`,
        method: 'DELETE',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const markReviewed = useCallback(
    async (airKeyPersonId, reviewed = true) => {
      const response = await sendRequest({
        url: `/api/v1/airkey/review/${airKeyPersonId}`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { reviewed },
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const deactivatePerson = useCallback(
    async airKeyPersonId => {
      const response = await sendRequest({
        url: `/api/v1/airkey/deactivate/${airKeyPersonId}`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const activateForUser = useCallback(
    async userId => {
      const response = await sendRequest({
        url: `/api/v1/airkey/activate/${userId}`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const reactivateForUser = useCallback(
    async userId => {
      const response = await sendRequest({
        url: `/api/v1/airkey/reactivate/${userId}`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const resyncForUser = useCallback(
    async userId => {
      const response = await sendRequest({
        url: `/api/v1/airkey/resync/${userId}`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const fetchAbgleich = useCallback(
    async ({ page = 1, limit = 50, search = '', onlyDiffs = false } = {}) => {
      const response = await sendRequest({
        url: '/api/v1/airkey/abgleich',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params: { page, limit, search, onlyDiffs },
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const fetchInactiveLinked = useCallback(
    async ({ page = 1, limit = 50, search = '' } = {}) => {
      const response = await sendRequest({
        url: '/api/v1/airkey/inactive-linked',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params: { page, limit, search },
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  const checkMappingsForUser = useCallback(
    async userId => {
      const response = await sendRequest({
        url: `/api/v1/airkey/check-mappings/${userId}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    },
    [sendRequest, authCtx]
  );

  return {
    isLoading,
    error,
    sync,
    autoMatch,
    manualMatch,
    unmatch,
    fetchPersons,
    fetchPersonProtocol,
    fetchByUserId,
    fetchAll,
    fetchAreas,
    fetchBranchMappings,
    upsertBranchMapping,
    deleteBranchMapping,
    markReviewed,
    deactivatePerson,
    activateForUser,
    reactivateForUser,
    resyncForUser,
    fetchAbgleich,
    fetchInactiveLinked,
    checkMappingsForUser,
  };
};

export default useAirKey;
